import React, { useEffect, useState } from "react";
import sanityClient from "../sanityClient";
import styled from "styled-components";
import { Helmet } from "react-helmet";
import eventFlyer from "../assets/images/hammer/flyer.webp"; // Placeholder flyer image
import eventPhoto1 from "../assets/images/hammer/1.webp";
import eventPhoto2 from "../assets/images/hammer/2.webp";
import eventPhoto3 from "../assets/images/hammer/3.webp";
import eventPhoto4 from "../assets/images/hammer/4.webp";
import eventPhoto5 from "../assets/images/hammer/5.webp";
import eventPhoto6 from "../assets/images/hammer/6.webp";
import eventPhoto7 from "../assets/images/hammer/7.webp";
import eventPhoto8 from "../assets/images/hammer/8.webp";
import eventPhoto9 from "../assets/images/hammer/9.webp";
import eventPhoto10 from "../assets/images/hammer/10.webp";
import eventPhoto11 from "../assets/images/hammer/11.webp";
import eventPhoto12 from "../assets/images/hammer/12.webp";

const HammerAndAlesPage = () => {
  const [breweries, setBreweries] = useState([]);
  const [foodVendors, setFoodVendors] = useState([]);
  const [sponsors, setSponsors] = useState([]);

  useEffect(() => {
    sanityClient
      .fetch(`*[_type == "hammerAndAlesBrewery"]{name, website}`)
      .then((data) => setBreweries(data))
      .catch(console.error);

    sanityClient
      .fetch(`*[_type == "hammerAndAlesFoodVendor"]{name, website}`)
      .then((data) => setFoodVendors(data))
      .catch(console.error);

    sanityClient
      .fetch(
        `*[_type == "hammerAndAlesSponsor"]{
        name, 
        tier, 
        "imageUrl": logo.asset->url, 
        website
      }`
      )
      .then((data) => setSponsors(data))
      .catch(console.error);
  }, []);

  const sponsorTiers = ["Kegerator", "Keg", "Growler", "Stein", "Pint"];

  const sponsorsByTier = sponsorTiers.reduce((acc, tier) => {
    acc[tier] = sponsors.filter((sponsor) => sponsor.tier === tier);
    return acc;
  }, {});

  return (
    <>
      <Helmet>
        <title>Hammer & Ales 2025 - Habitat for Humanity Buffalo</title>
        <meta
          name="description"
          content="Join us for the 7th Annual Hammer & Ales – a night of flannel, fun, and fundraising to support Habitat Buffalo!"
        />
      </Helmet>

      <Container>
        <Header>
          <h1>Hammer & Ales 2025</h1>
          <FlyerImage src={eventFlyer} alt="Hammer & Ales 2025 Flyer" />
          <p>
            Join us for the 7th Annual Hammer & Ales, Habitat for Humanity
            Buffalo’s most exciting fundraiser of the year! This incredible
            night brings together the best of Buffalo’s craft beer scene, giving
            you access to unlimited tastings from top local breweries and
            beverage vendors. But that’s just the beginning—your ticket also
            includes a wide selection of delicious, complimentary food from some
            of the area's best restaurants and food vendors.
          </p>
          <p>
            Enjoy a night filled with interactive games, exciting raffles, and
            crowd-favorite competitions like the legendary Flannel Fashion
            Showdown. Get ready to dance to live music, capture the fun at our
            photo booth, and test your luck at the wine toss, stump game, and
            other hands-on activities.
          </p>
          <p>
            And the best part? Every sip, bite, and laugh helps support Habitat
            Buffalo’s House That Beer Built campaign, raising crucial funds to
            build safe, affordable homes for families in need—right here in
            Buffalo. Grab your friends, put on your best flannel, and come be
            part of something amazing!
          </p>
          <TicketsButton
            href="https://habitatbuffalo.betterworld.org/events/hammerandales2025"
            target="_blank"
            rel="noopener noreferrer"
          >
            Get Your Tickets
          </TicketsButton>
        </Header>

        <EventPhotos>
          <EventPhoto src={eventPhoto1} alt="Event photo 1" position="top" />
          <EventPhoto src={eventPhoto9} alt="Event photo 1" />
          <EventPhoto src={eventPhoto3} alt="Event photo 3" />
          <EventPhoto src={eventPhoto4} alt="Event photo 4" />
        </EventPhotos>

        <Section bgColor="#fff9db">
          <h2>🍺 Breweries & Beverage Vendors</h2>
          <p>
            Your ticket to Hammer & Ales includes a custom tasting glass and
            unlimited samples from all participating breweries and beverage
            vendors. Enjoy a handpicked selection of local craft beers, ciders,
            and non-alcoholic drinks from some of the best makers in Western New
            York. Explore unique flavors, chat with the brewers, and discover
            new favorites—all included with your admission!
          </p>
          <TwoColumnList>
            {breweries.map((brewery) => (
              <ListItem key={brewery.name}>
                {brewery.website ? (
                  <a
                    href={brewery.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {brewery.name}
                  </a>
                ) : (
                  brewery.name
                )}
              </ListItem>
            ))}
          </TwoColumnList>
        </Section>

        <EventPhotos>
          <EventPhoto src={eventPhoto5} alt="Event photo 1" position="top" />
          <EventPhoto src={eventPhoto6} alt="Event photo 2" position="top" />
          <EventPhoto src={eventPhoto7} alt="Event photo 3" position="top" />
          <EventPhoto src={eventPhoto8} alt="Event photo 4" position="top" />
        </EventPhotos>

        <Section bgColor="#eaf4fc">
          <h2>🍽️ Food Vendors</h2>
          <p>
            No great event is complete without amazing food! Throughout the
            night, enjoy complimentary bites from some of Buffalo’s top food
            vendors, offering a variety of savory and sweet options to keep you
            fueled while you enjoy the event. Whether you're in the mood for
            classic comfort food or something unique, our lineup of vendors has
            something for everyone.
          </p>
          <TwoColumnList>
            {foodVendors.map((vendor) => (
              <ListItem key={vendor.name}>
                {vendor.website ? (
                  <a
                    href={vendor.website}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {vendor.name}
                  </a>
                ) : (
                  vendor.name
                )}
              </ListItem>
            ))}
          </TwoColumnList>
        </Section>

        <EventPhotos>
          <EventPhoto src={eventPhoto9} alt="Event photo 1" />
          <EventPhoto src={eventPhoto10} alt="Event photo 2" />
          <EventPhoto src={eventPhoto11} alt="Event photo 3" />
          <EventPhoto src={eventPhoto12} alt="Event photo 4" />
        </EventPhotos>

        <Section bgColor="#ffffff">
          <h2>🏆 Sponsors</h2>
          {sponsorTiers.map((tier, index) =>
            sponsorsByTier[tier] && sponsorsByTier[tier].length > 0 ? (
              <TierSection key={tier}>
                <h3>{tier} Level</h3>
                <SponsorGrid>
                  {sponsorsByTier[tier].map((sponsor) => (
                    <SponsorItem
                      key={sponsor.name}
                      isHarmac={sponsor.name === "Harmac Medical Products"}
                    >
                      {sponsor.imageUrl && (
                        <SponsorLogo
                          src={sponsor.imageUrl}
                          alt={sponsor.name}
                          size={
                            index === 0
                              ? "kegerator"
                              : index === 1
                              ? "large"
                              : index === 2
                              ? "medium"
                              : index === 3
                              ? "small"
                              : "extra-small"
                          }
                        />
                      )}
                      <SponsorName>{sponsor.name}</SponsorName>
                      {sponsor.website && (
                        <a
                          href={sponsor.website}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Visit Website
                        </a>
                      )}
                    </SponsorItem>
                  ))}
                </SponsorGrid>
              </TierSection>
            ) : null
          )}
        </Section>
      </Container>
    </>
  );
};

/* Styled Components */
const Container = styled.div`
  padding: 20px;
  max-width: 1200px;
  margin: auto;
`;

const Header = styled.header`
  text-align: center;
  margin-bottom: 40px;
`;

const FlyerImage = styled.img`
  width: 100%;
  max-width: 600px;
  display: block;
  margin: 20px auto;
`;

const TicketsButton = styled.a`
  display: inline-block;
  background-color: var(--hhb-primary);
  color: var(--hhb-white);
  padding: 15px 25px;
  font-size: 18px;
  font-weight: bold;
  border-radius: 5px;
  text-decoration: none;
  &:hover {
    background-color: var(--hhb-secondary);
  }
`;

const SponsorGrid = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap;
  gap: 30px;
  align-items: center;
`;

const SponsorItem = styled.div`
  text-align: center;
  width: ${({ isHarmac }) => (isHarmac ? "100%" : "350px")};
  display: ${({ isHarmac }) => (isHarmac ? "flex" : "block")};
  justify-content: ${({ isHarmac }) => (isHarmac ? "center" : "initial")};
  align-items: center;
  flex-direction: column;
`;

const SponsorLogo = styled.img`
  width: ${({ size }) =>
    size === "kegerator"
      ? "500px"
      : size === "large"
      ? "250px"
      : size === "medium"
      ? "200px"
      : size === "small"
      ? "150px"
      : "120px"};
  height: ${({ size }) =>
    size === "kegerator"
      ? "auto"
      : size === "large"
      ? "150px"
      : size === "medium"
      ? "130px"
      : size === "small"
      ? "110px"
      : "100px"};
  object-fit: contain;
  display: block;
  margin: 0 auto;
`;

const SponsorName = styled.h4`
  text-align: center;
  margin-top: 10px;
`;

const EventPhotos = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
`;

const EventPhoto = styled.img`
  flex: 1;
  width: 24%;
  min-height: 200px; /* Ensures minimum height */
  height: auto; /* Allows flexibility */
  max-height: 300px; /* Prevents excessive stretching */
  object-fit: cover; /* Keeps aspect ratio while filling */
  object-position: ${({ position }) => position || "center"}; /* Adjust crop */
  border-radius: 5px;

  @media (max-width: 768px) {
    width: 48%; /* Two per row on tablets */
  }

  @media (max-width: 480px) {
    width: 100%; /* Full width on mobile */
  }
`;

const Section = styled.section`
  padding: 40px;
  background-color: ${(props) => props.bgColor};
  margin-bottom: 40px;
  text-align: center;
`;

const TwoColumnList = styled.ul`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
  list-style: none;
  padding: 0;
  margin-top: 20px;
`;

const ListItem = styled.li`
  font-size: 18px;
  margin-bottom: 10px;
`;

const TierSection = styled.div`
  margin-bottom: 40px;
  text-align: center;
`;

export default HammerAndAlesPage;
